import * as React from 'react';
import { Link, useRouterState } from '@tanstack/react-router';

import { NavMain } from '@/components/nav-main';
import { NavUser } from '@/components/nav-user';
import { NavActions } from '@/components/nav-actions';
import { OrgSwitcher } from '@/components/org-switcher';
import { useFilteredNavigation } from '@/hooks/useFilteredNavigation';
import { mainNavigation, quickStartNavGroup } from '@/config/navigation';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from '@/components/ui/sidebar';

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const router = useRouterState();
  const currentPath = router.location.pathname;
  const filteredNavigation = useFilteredNavigation(mainNavigation);

  // Quick Start goes through the same filter as everything else, so the CTA
  // obeys the same edition rule the nav list would have applied. Empty in
  // personal edition, where the group filters down to nothing.
  const [quickStartGroup] = useFilteredNavigation(quickStartNavGroup);
  const quickStart = quickStartGroup?.items[0];

  return (
    <Sidebar variant="inset" {...props}>
      <SidebarHeader>
        <OrgSwitcher />

        {/*
          Promoted above the nav rather than sitting last in the Clinical group.
          Starting a recording is the app's primary action and it was reading as
          the fifth of five list items.

          Built on SidebarMenuButton so the collapsed 76px rail comes for free:
          the label is hidden and the tooltip takes over, exactly as the nav
          items do.
        */}
        {quickStart && (
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                tooltip={quickStart.title}
                className="bg-primary font-medium text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground active:bg-primary/90 active:text-primary-foreground">
                <Link to={quickStart.href}>
                  <quickStart.icon />
                  <span>{quickStart.title}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        )}
      </SidebarHeader>

      <SidebarContent>
        <NavMain currentPath={currentPath} groups={filteredNavigation} />
      </SidebarContent>

      <SidebarFooter>
        <NavActions />
        <NavUser />
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
